import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, Phone, PhoneIncoming, PhoneOutgoing, MessageSquare, 
  User, ChevronDown, ChevronUp, UserPlus, Clock, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

const UnassignedCallersPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [unassignedContacts, setUnassignedContacts] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState({});
  const [contactHistories, setContactHistories] = useState({});
  const [assigningContactId, setAssigningContactId] = useState(null);
  const [showAssignDropdown, setShowAssignDropdown] = useState(null);
  const [loading, setLoading] = useState(true);

  // Affiliates may not access this page at all.
  useEffect(() => {
    if (user && !['admin', 'supervisor', 'lead'].includes((user.role || '').toLowerCase())) {
      navigate('/calls', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUnassigned();
    fetchEligibleUsers();
  }, []);

  // Mobile header — title "Unassigned Callers" + back arrow to /calls.
  // The original "[x] unassigned contact(s)" subtitle is dropped;
  // users can scan the list to see the count.
  usePageHeader({ title: 'Unassigned Callers', back: '/calls' }, []);

  const fetchUnassigned = async () => {
    setLoading(true);
    try {
      const res = await api.get('/twilio/contacts/unassigned');
      setUnassignedContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch unassigned contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleUsers = async () => {
    try {
      const res = await api.get('/twilio/contacts/eligible-users');
      setEligibleUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch eligible users:', error);
    }
  };

  const fetchContactHistory = async (contactId) => {
    try {
      const res = await api.get(`/twilio/contacts/${contactId}/history`);
      setContactHistories(prev => ({
        ...prev,
        [contactId]: {
          calls: res.data.calls || [],
          messages: res.data.messages || []
        }
      }));
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleExpandContact = (contactId) => {
    if (expandedContactId === contactId) {
      setExpandedContactId(null);
      return;
    }
    setExpandedContactId(contactId);
    if (!contactHistories[contactId]) {
      fetchContactHistory(contactId);
    }
    if (!activeHistoryTab[contactId]) {
      setActiveHistoryTab(prev => ({ ...prev, [contactId]: 'calls' }));
    }
  };

  const handleAssignContact = async (contactId, assignedUserId) => {
    setAssigningContactId(contactId);
    try {
      await api.post(`/twilio/contacts/${contactId}/assign`, {
        assignment_type: 'individual',
        assigned_to_user_id: assignedUserId
      });
      const assignedUser = eligibleUsers.find(u => u.id === assignedUserId);
      const name = assignedUser ? getFullName(assignedUser) : 'user';
      toast.success(`Contact assigned to ${name}`);
      setShowAssignDropdown(null);
      fetchUnassigned();
    } catch (error) {
      toast.error('Failed to assign contact');
    } finally {
      setAssigningContactId(null);
    }
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name} ${u.last_name}`;
    return u?.full_name || 'Unknown';
  };

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    if (u?.full_name) return u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return 'U';
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 border-slate-500/20';
    }
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Unknown';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24 space-y-4" data-testid="unassigned-callers-page">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3" />
          <span className="text-muted-foreground text-sm">Loading...</span>
        </div>
      ) : unassignedContacts.length === 0 ? (
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <UserPlus className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground font-medium">No unassigned callers</p>
            <p className="text-xs text-muted-foreground mt-1">
              When someone calls or texts the state number without being assigned, they'll appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {unassignedContacts.map((contact) => {
            const isExpanded = expandedContactId === contact.id;
            const history = contactHistories[contact.id];
            const tab = activeHistoryTab[contact.id] || 'calls';

            return (
              <Card key={contact.id} className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg overflow-hidden" data-testid={`unassigned-contact-${contact.id}`}>
                <CardContent className="p-0">
                  {/* Contact Header */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    onClick={() => handleExpandContact(contact.id)}
                  >
                    <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{contact.name || formatPhone(contact.phone_number)}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{formatPhone(contact.phone_number)}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-50 text-orange-600 border-orange-200">
                          {contact.state}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {contact.last_contact_at && (
                        <span className="text-[10px] text-muted-foreground">{formatDate(contact.last_contact_at)}</span>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {/* Expanded: History + Assign */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 space-y-3">
                      {/* History Tabs */}
                      <div className="flex rounded-lg bg-[#f5f5f5] dark:bg-[#0d0d0d] p-1">
                        {['calls', 'messages'].map((t) => (
                          <button
                            key={t}
                            onClick={() => setActiveHistoryTab(prev => ({ ...prev, [contact.id]: t }))}
                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                              tab === t
                                ? 'bg-white dark:bg-[#1a1a1a] shadow-sm text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid={`history-tab-${t}-${contact.id}`}
                          >
                            {t === 'calls' ? <Phone className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                            {t === 'calls' ? 'Call History' : 'Message History'}
                          </button>
                        ))}
                      </div>

                      {/* History Content */}
                      <ScrollArea className="max-h-48">
                        {!history ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : tab === 'calls' ? (
                          history.calls.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">
                              <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
                              <p className="text-xs">No call history</p>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {history.calls.map((call) => (
                                <div key={call.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                                  {call.direction === 'inbound' ? (
                                    <PhoneIncoming className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium capitalize">{call.direction} call</p>
                                    <p className="text-[10px] text-muted-foreground">{formatDate(call.created_at)}</p>
                                  </div>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{call.status}</Badge>
                                </div>
                              ))}
                            </div>
                          )
                        ) : (
                          history.messages.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">
                              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                              <p className="text-xs">No message history</p>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {history.messages.map((msg) => (
                                <div key={msg.id} className={`p-2 rounded-lg max-w-[85%] ${
                                  msg.direction === 'inbound' 
                                    ? 'bg-white dark:bg-[#2a2a2a] border border-gray-100 dark:border-gray-700' 
                                    : 'bg-[#586D52] text-white ml-auto'
                                }`}>
                                  <p className="text-xs">{msg.body}</p>
                                  <p className={`text-[10px] mt-0.5 ${msg.direction === 'inbound' ? 'text-muted-foreground' : 'text-white/70'}`}>
                                    {formatDate(msg.created_at)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </ScrollArea>

                      {/* Assign Button */}
                      <div className="relative">
                        <Button
                          className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                          size="sm"
                          onClick={() => setShowAssignDropdown(showAssignDropdown === contact.id ? null : contact.id)}
                          disabled={assigningContactId === contact.id}
                          data-testid={`assign-btn-${contact.id}`}
                        >
                          {assigningContactId === contact.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <UserPlus className="w-4 h-4 mr-2" />
                          )}
                          Assign Contact
                        </Button>

                        {showAssignDropdown === contact.id && (
                          <div className="absolute bottom-full mb-1 left-0 right-0 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-10 max-h-48 overflow-y-auto">
                            {eligibleUsers.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-3">No eligible users found</p>
                            ) : (
                              eligibleUsers.map((u) => (
                                <button
                                  key={u.id}
                                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                                  onClick={() => handleAssignContact(contact.id, u.id)}
                                  data-testid={`assign-to-${u.id}`}
                                >
                                  <Avatar className="h-7 w-7">
                                    <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(u.role)} text-white text-[10px]`}>
                                      {getInitials(u)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{getFullName(u)}</p>
                                  </div>
                                  <Badge className={`text-[10px] capitalize px-1.5 py-0 border ${getRoleBadgeStyle(u.role)}`}>
                                    {u.role}
                                  </Badge>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UnassignedCallersPage;
